import { FC, useEffect, memo } from 'react';
import { FetchResult } from '@apollo/client';

import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import AnalyticsService, {
  StreamBindFlowEventNameAMP,
  StreamBindFlowParamNameAMP,
} from 'application/services/AnalyticsService';
import usePlayerChangeController from 'application/hooks/usePlayerChangeController';
import { useOnboardingActionContext, useOnboardingContext } from 'application/components/common';
import { useAccountCompanyId } from 'application/hooks';
import { fetchCurrentUserData } from '@entities/account';
import { selectIsVideoAvailable } from '@shared/lib/featureFlags';
import { useAppDispatch, useAppSelector } from '@shared/store';

import {
  CompanyAudioDeviceRequestMutation,
  CompanyVideoDeviceRequestMutation,
  DeviceTypes,
  RequestDeviceType,
  useCompanyAudioDeviceRequestMutation,
  useCompanyVideoDeviceRequestMutation,
} from '@shared/api/graphql/generated';

import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import IconSettings from '@zvuk-b2b/react-uikit/ui/icons/Settings';
import IconWifiOff from '@zvuk-b2b/react-uikit/ui/icons/WifiOff';
import Popper, { HintPlacement } from '@zvuk-b2b/react-uikit/ui/Popper';
import IconMusic from '@zvuk-b2b/react-uikit/ui/icons/Music';
import IconPlayCircle from '@zvuk-b2b/react-uikit/ui/icons/PlayCircle';

import { TEXTS } from './texts';
import useSberBusinessPlayerModalViewModel from './useSberBusinessPlayerModalViewModel';
import { DeviceTypesInFlow, PlayerTypesInFlow } from '../../types';
import { FLOWS_FOR_NOT_SHOW_STOP_ONBOARDING_WARNING } from '../../consts';

import styles from '../index.module.scss';

const DEVICE_IMAGE_PATH = '/assets/img/device_photos/sberBussiness.png';

const SberBussinessPlayerModal: FC<ModalProps[ModalTypes.SBERBUSSINESS_REQUEST]> = ({
  requestType = RequestDeviceType.Audio,
  close,
  openModal,
  replaceModal,
}) => {
  const { flowType, currentPoint, selectedPlayerInFlow } = useOnboardingContext();
  const { clearCurrentPoint } = useOnboardingActionContext();

  const companyId = useAccountCompanyId();

  const [requestAudioMutation, { loading: requestAudioLoading }] = useCompanyAudioDeviceRequestMutation();
  const [requestVideoMutation, { loading: requestVideoLoading }] = useCompanyVideoDeviceRequestMutation();

  const dispatch = useAppDispatch();

  const { onSuccessCallback, hasRecentRequest, isFirstRequest, cantRequestDevice } =
    useSberBusinessPlayerModalViewModel({
      point: currentPoint,
      modalDeviceType: DeviceTypesInFlow.ANDROIDBOX,
      requestType,
    });

  const isVideoAvailable = useAppSelector(selectIsVideoAvailable);

  const { setToPlayerType } = usePlayerChangeController({
    pointId: currentPoint?.id || '',
    fromPlayerType: DeviceTypes.Androidbox,
    onSuccessCallback,
  });

  useEffect(() => {
    setToPlayerType('WEB');
  }, []);

  const onSubmit = async () => {
    AnalyticsService.event(StreamBindFlowEventNameAMP.STREAM_REQUEST_DEVICE_CLICKED, {
      [StreamBindFlowParamNameAMP.DEVICE_TYPE]: PlayerTypesInFlow.ANDROIDBOX,
      [StreamBindFlowParamNameAMP.FIRST_DEVICE_REQUEST]: isFirstRequest,
      [StreamBindFlowParamNameAMP.DEVICE_REQUEST_TYPE]: requestType,
    });

    const mutation = requestType === RequestDeviceType.Audio ? requestAudioMutation : requestVideoMutation;

    const result = await mutation({
      variables: {
        input: {
          companyId,
          deviceTypes: [DeviceTypes.Androidbox],
          pointId: currentPoint?.id || '',
        },
      },
    });

    const isOk =
      requestType === RequestDeviceType.Audio
        ? (result as FetchResult<CompanyAudioDeviceRequestMutation>).data?.companyCreateAudioDeviceRequest?.ok
        : (result as FetchResult<CompanyVideoDeviceRequestMutation>).data?.companyCreateVideoDeviceRequest?.ok;

    if (isOk) {
      dispatch(fetchCurrentUserData());
      replaceModal(ModalTypes.DEVICE_REQUESTED, {});
    }
  };

  const onConfirmClose = () => {
    if (flowType && FLOWS_FOR_NOT_SHOW_STOP_ONBOARDING_WARNING.has(flowType)) {
      close();
      clearCurrentPoint();
      return;
    }

    openModal(ModalTypes.STREAM_STOP_BINDING_FLOW_WARNING, {});
  };

  const handleCancel = () => {
    if (hasRecentRequest) {
      clearCurrentPoint();
      close();
    } else {
      onConfirmClose();
    }
  };

  const handleBack = () => {
    replaceModal(
      selectedPlayerInFlow === PlayerTypesInFlow.VIDEOPLAYER
        ? ModalTypes.CHOOSE_VIDEO_PLAYER
        : ModalTypes.SELECT_PLAYER_MODAL,
      {}
    );
  };

  const SubmitButton = (
    <Button
      key="button_sberbussiness_player_request_modal_success"
      elementId="button_sberbussiness_player_request_modal_success"
      type={ButtonType.PRIMARY}
      onClick={onSubmit}
      isLoading={requestAudioLoading || requestVideoLoading}
      isDisabled={hasRecentRequest || cantRequestDevice}>
      {hasRecentRequest ? TEXTS.SUCCESS_DISABLED_BUTTON : TEXTS.SUCCESS_BUTTON}
    </Button>
  );

  const CancelButton = (
    <Button
      key="button_sberbussiness_player_request_modal_cancel"
      elementId="button_sberbussiness_player_request_modal_cancel"
      type={ButtonType.SECONDARY}
      isLoading={requestAudioLoading || requestVideoLoading}
      onClick={handleBack}>
      {TEXTS.CANCEL_BUTTON}
    </Button>
  );

  const SubmitButtonWithHint = (
    <Popper
      containerClassName={styles.DeviceModalHint}
      placement={HintPlacement.PLACEMENT_TOP_START}
      triggerNode={SubmitButton}>
      {TEXTS.DISABLED_BUTTON_BINDED_HINT}
    </Popper>
  );

  return (
    <ModalScreen
      titlePlacement="left"
      onClose={handleCancel}
      className={styles.DeviceModal}
      contentClassName={styles.DeviceModalContent}
      containerClassName={styles.DeviceModalContainer}
      actionButtons={[cantRequestDevice ? SubmitButtonWithHint : SubmitButton, CancelButton]}>
      <img
        src={DEVICE_IMAGE_PATH}
        alt=""
        loading="lazy"
        className={styles.DeviceModalImage}
      />

      <h2>{selectedPlayerInFlow === PlayerTypesInFlow.VIDEOPLAYER ? TEXTS.MODAL_VIDEO_TITLE : TEXTS.MODAL_TITLE}</h2>

      <div>{TEXTS.MODAL_AUDIO_DESCRIPTION}</div>

      <ul>
        <li>
          <IconWifiOff />
          {TEXTS.FEATURES_INTERNET}
        </li>
        <li>
          <IconSettings />
          {TEXTS.FEATURES_DEVICES}
        </li>
        <li>
          <IconMusic />
          {TEXTS.FEATURES_AUDIO}
        </li>
        {isVideoAvailable && (
          <li>
            <IconPlayCircle />
            {TEXTS.FEATURES_VIDEO}
          </li>
        )}
      </ul>
    </ModalScreen>
  );
};

export default memo(SberBussinessPlayerModal);
